/* eslint-disable react-hooks/exhaustive-deps */
import { useNavigate } from 'react-router-dom';
import { useContext, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { RootState } from '../../../Redux/Store';
import { AppContext } from '../../../Context/AppContext';
import { useAppDispatch, useAppSelector } from '../../../Hooks/Hooks';
import {
  getFavoriteSearch,
  getSearchLoads,
  handleResetPaginationFilter,
  handleSelectedTopCardData,
  updateFavoriteSearch,
  updateSearchQuery,
  updateSearchResultData,
} from '../../../Redux/Reducers/SearchSlice';
import { EVENT, SCREEN, TAB } from '../../../Constants/AnalyticsConstants';
import { fbLogEvent } from '../../../Helpers/FirebaseHelper';
import { dataBaseFriendlyDateFormat, getNewDates } from '../../../Utils/DateUtils';
import { isAdvanceSearch } from '../../../Utils/ContentOrConfigUtil';
import {
  ANYWHERE,
  FAVORITE_SEARCH_ADDED,
  FAVORITE_SEARCH_REMOVED,
  KEYS,
  SERVER_ERROR_MESSAGES,
  STATES,
} from '../../../Constants/Constants';
import { TFavoriteSearchContainerProps } from './FavoriteSearchContainer.type';
import { ELoadCardSize } from '../../Shared/LoadCard/LoadCard.type';
import LoadCard from '../../Shared/LoadCard/LoadCard';
import './FavoriteSearchContainer.scss';

const FavoriteSearchContainer = (props: TFavoriteSearchContainerProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { isMobile } = useContext(AppContext);
  const { memberData } = useAppSelector((state: RootState) => state.member);
  const { sortAndFilter } = useAppSelector((state: RootState) => state.search);
  const { favoriteSearchData } = useAppSelector((state: RootState) => state?.search);
  const { configTableData: { FPO_APP_CONFIG: { APP_INIT } = { APP_INIT: {} } } = {} } =
    useAppSelector((state: RootState) => state.ContentOrConfig);

  const [favoriteSearchPageData] = useState({
    page: 0,
    size: KEYS?.FAV_SEARCHES_PAGE_MAX_LOAD_CAPACITY,
    sortColumn: 'pickupdate',
  });

  const hasAdvancedSearch = isAdvanceSearch(APP_INIT, 'favoriteSearchContainer');

  useEffect(() => {
    const queryPayloads = {
      carrierId: memberData?.member?.userApplicationDetails?.[0].attributes.CommonCarrierId ?? null,
      userId: memberData?.member?.upn ?? null,
      favorite: true,
      ...favoriteSearchPageData,
    };
    dispatch(getFavoriteSearch(queryPayloads));
  }, [
    dispatch,
    memberData?.member?.userApplicationDetails?.[0]?.attributes?.CommonCarrierId,
    memberData?.member?.upn,
    favoriteSearchPageData,
  ]);

  const handleRightTopButtonClick = (id: string) => {
    const item = favoriteSearchData?.find((item: any) => item?.id === id);
    const queryPayloads = {
      ...item,
      favouriteSearchIndicator: !item?.favouriteSearchIndicator,
    };
    const callback = (res: any) => {
      if (res?.status) {
        const _queryPayloads = {
          carrierId:
            memberData?.member?.userApplicationDetails?.[0].attributes.CommonCarrierId ?? null,
          userId: memberData?.member?.upn ?? null,
          favorite: true,
          ...favoriteSearchPageData,
        };
        toast(
          queryPayloads?.favouriteSearchIndicator ? FAVORITE_SEARCH_ADDED : FAVORITE_SEARCH_REMOVED,
        );
        dispatch(getFavoriteSearch(_queryPayloads));
      } else {
        toast(res?.errorMessage ?? SERVER_ERROR_MESSAGES?.generalMsg);
      }
    };
    dispatch(updateFavoriteSearch({ queryPayloads, callback }));
    fbLogEvent(EVENT.UE_MARK_UNFAVORITE, {
      screen: SCREEN.SEARCH,
      tab: TAB.FAVORITE,
      origin: `${(item?.originCity ?? ANYWHERE) + '_'}${item?.originState ?? ''}`,
      destination: `${item?.destinationCity + '_'}${[
        item?.destinationState1,
        item?.destinationState2,
        item?.destinationState3,
      ]?.filter((item: any) => !!item)}`,
    });
  };
  const handleClick = (id: string) => {
    dispatch(
      updateSearchResultData({
        totalRecords: 0,
        loadPostings: [],
      }),
    );
    const item = favoriteSearchData?.find((item: any) => item?.id === id);
    dispatch(handleSelectedTopCardData(item));

    const pickUpDates = getNewDates(item?.pickupStartDate, item?.pickupEndDate, 'origin');
    const dropDates = getNewDates(item?.dropStartDate, item?.dropEndDate, 'destination');

    const destinationStates = item?.destinationCity
      ? item?.destinationState1
      : [item?.destinationState1, item?.destinationState2, item?.destinationState3]
          ?.filter((state) => !!state)
          ?.map((state) => STATES?.find((item: any) => item.code === state));
    const queryPayloads = {
      ...item,
      originLocation: {
        Country: item?.originCountry,
        City: item?.originCity,
        State: item?.originState,
        Zip: item?.originPostalCode,
      },
      originRadius: item?.originRadius,
      originFromDate: pickUpDates?.newStartDate,
      originToDate: pickUpDates?.newEndDate,
      destinationLocation: {
        Country: item?.destinationCountry,
        City: item?.destinationCity,
        State: destinationStates,
        Zip: item?.destinationPostalCode,
      },
      destinationRadius: item?.destinationRadius,
      destinationFromDate: dropDates?.newStartDate,
      destinationToDate: dropDates?.newEndDate,
    };
    dispatch(updateSearchQuery(queryPayloads));
    dispatch(handleResetPaginationFilter());
    const payload = {
      size: sortAndFilter?.size,
      page: 0,
      sortColumn: sortAndFilter?.sortColumn,
      sortDirection: sortAndFilter?.sortDirection,
      originCity: item?.originCity ?? ANYWHERE,
      originState: item?.originState,
      originRadius: item?.originRadius,
      originCountry: item?.originCountry,
      originPostalCode: item?.originPostalCode,
      pickUpStartDate:
        dataBaseFriendlyDateFormat(
          pickUpDates?.newStartDate,
          'startDate',
          item?.timeZone || 'GMT',
        ) ?? '',
      pickUpEndDate:
        dataBaseFriendlyDateFormat(pickUpDates?.newEndDate, 'endDate', item?.timeZone || 'GMT') ??
        '',
      dropStartDate:
        dataBaseFriendlyDateFormat(dropDates?.newStartDate, 'startDate', item?.timeZone || 'GMT') ??
        '',
      dropEndDate:
        dataBaseFriendlyDateFormat(dropDates?.newEndDate, 'endDate', item?.timeZone || 'GMT') ?? '',
      destinationCity: item?.destinationCity,
      destinationStates: !item?.destinationCity
        ? destinationStates?.map((_state: any) => _state?.code)
        : destinationStates
          ? [destinationStates]
          : [],
      destinationCountry: item?.destinationCountry || '',
      destinationPostalCode: item?.destinationPostalCode || '',
      destinationRadius: item?.destinationRadius,
      excludeSpecialServices: item?.excludeSpecialServices,
      maxMiles: item?.maxMiles,
      minMiles: item?.minMiles,
      minRPM: item?.minRPM,
      appointmentConfirmed: null,
      extendedNetwork: null,
    };
    dispatch(getSearchLoads({ queryPayloads: payload }));
    fbLogEvent(EVENT.UE_SEARCH_RESULTS, {
      screen: SCREEN.SEARCH,
      tab: TAB.FAVORITE,
      origin: `${(item?.originCity ?? ANYWHERE) + '_'}${item?.originState ?? ''}`,
      destination: `${item?.destinationCity + '_'}${[
        item?.destinationState1,
        item?.destinationState2,
        item?.destinationState3,
      ]?.filter((item: any) => !!item)}`,
    });
    isMobile ? navigate('/search/results') : navigate('/search/favorites');
  };

  return (
    <div
      className={`FavoriteSearchContainer ${!!favoriteSearchData?.length ? 'visible' : 'hidden'}`}
      data-testid="FavoriteSearchContainer"
    >
      {!!favoriteSearchData?.length &&
        favoriteSearchData?.map((item: any) => (
          <LoadCard
            key={item?.id}
            data={item}
            isFavorite={true}
            handleRightTopButtonClick={handleRightTopButtonClick}
            handleBottomClick={() => handleClick(item?.id)}
            handleCardClick={() => handleClick(item?.id)}
            showBottomButton={true}
            size={ELoadCardSize.medium}
            showAdvancedSearchData={hasAdvancedSearch}
            withoutDateTime={true}
          />
        ))}
    </div>
  );
};
export default FavoriteSearchContainer;
